import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { TasksSectionContent } from "@/pages/tasks/components/TasksSectionContent.tsx";
import { TasksTodoCountText } from "@/pages/tasks/components/TasksTodoCountText.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  useEmployeeTodoTasks,
  useEmployeeTasksSearch,
} from "@/queries/employees/use-employee-tasks.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiTaskEmployeeRole, ApiTaskFilter, ApiTaskStatus } from "@/types/task.types.ts";
import { TaskEditModal } from "@/pages/tasks/widgets/TaskEditModal.tsx";
import { useOpenTaskFromUrl, useTaskDueSplit } from "@/pages/tasks/hooks/tasks.hooks.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  EmptyStateBase,
  FontAwesomeIcon,
  PaginationBlock,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useSearchParams } from "react-router-dom";

interface EmployeeTasksProps extends EmployeePageComponentProps {
  role: ApiTaskEmployeeRole;
}

const titleKeyMap: Record<ApiTaskEmployeeRole, string> = {
  [ApiTaskEmployeeRole.assignee]: "tasks:tasks_as_assignee",
  [ApiTaskEmployeeRole.related]: "tasks:tasks_as_related",
  [ApiTaskEmployeeRole.watcher]: "tasks:section_title_watching",
};

const emptyDescriptionKeyMap: Record<ApiTaskEmployeeRole, string> = {
  [ApiTaskEmployeeRole.assignee]: "tasks:section_empty_description_assignee",
  [ApiTaskEmployeeRole.related]: "tasks:section_empty_description_related",
  [ApiTaskEmployeeRole.watcher]: "tasks:section_empty_title_watching",
};

export function EmployeeTasks({ employee, role }: EmployeeTasksProps) {
  const { t } = useTranslation();
  const { renderMonthYear } = useFormatter();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const [, setSearchParams] = useSearchParams();
  const { data: employeeMe } = useEmployeeMe();

  const isOwnProfile = employeeMe?.id === employee.id;
  const library = filtersLibrary();

  useOpenTaskFromUrl();

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiTaskFilter>({
    filters: [library.page_number],
    source: "Employee Tasks",
  });

  const { data: todoTasks, isLoading: isTodoLoading } = useEmployeeTodoTasks(employee.id, role);
  const { data: completedData, isLoading: isCompletedLoading } = useEmployeeTasksSearch(
    employee.id,
    role,
    { ...filter, status: ApiTaskStatus.completed }
  );

  const { overdueTasks: overdueTodoTasks, upcomingTasks: upcomingTodoTasks } =
    useTaskDueSplit(todoTasks);

  const todoCount = todoTasks?.length ?? 0;
  const completedItems = completedData?.items ?? [];

  const completedGroups = completedItems.reduce<{ date: Date; tasks: typeof completedItems }[]>(
    (groups, task) => {
      const completed = task.datetime_completed ? new Date(task.datetime_completed) : new Date(0);
      const key = `${completed.getFullYear()}-${String(completed.getMonth() + 1).padStart(2, "0")}`;
      const existing = groups.find(
        (g) => `${g.date.getFullYear()}-${String(g.date.getMonth() + 1).padStart(2, "0")}` === key
      );
      if (existing) {
        existing.tasks.push(task);
      } else {
        groups.push({ date: completed, tasks: [task] });
      }
      return groups;
    },
    []
  );

  const canCreateTask = canIEdit(ApiPermission.tasks_create);

  const handleCreateTask = () => {
    if (role === ApiTaskEmployeeRole.assignee) {
      openModal(TaskEditModal, { task: null, assignToEmployee: employee });
    } else if (role === ApiTaskEmployeeRole.related) {
      openModal(TaskEditModal, { task: null, relatedEmployee: employee });
    } else {
      openModal(TaskEditModal, { task: null });
    }
  };

  const hasTodo = overdueTodoTasks.length > 0 || upcomingTodoTasks.length > 0;
  const hasCompleted = completedItems.length > 0;
  const hasAny = hasTodo || hasCompleted;
  const isAnyLoading = isCompletedLoading || isTodoLoading;

  return (
    <Stack gap="lg">
      <Stack direction="horizontal" justify="between">
        <Stack gap={"xs"}>
          <Title size={"lg"} header={t(titleKeyMap[role])} />
          {hasAny && (
            <TasksTodoCountText
              count={todoCount}
              variant={"secondary"}
              employee={isOwnProfile ? undefined : employee}
            />
          )}
        </Stack>
        {canCreateTask && (
          <Button onClick={handleCreateTask} leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
            {t("tasks:create_task")}
          </Button>
        )}
      </Stack>
      {hasAny || isAnyLoading ? (
        <Stack gap={"2xl"}>
          {(hasTodo || isTodoLoading) && (
            <Stack gap={"lg"}>
              {overdueTodoTasks.length > 0 && (
                <TasksSectionContent
                  title={t("tasks:section_title_overdue")}
                  titleWarning
                  tasks={overdueTodoTasks}
                  isLoading={isTodoLoading}
                />
              )}
              {upcomingTodoTasks.length > 0 && (
                <TasksSectionContent
                  title={t("tasks:section_title_todo")}
                  tasks={upcomingTodoTasks}
                  isLoading={isTodoLoading}
                />
              )}
            </Stack>
          )}
          {(hasCompleted || isCompletedLoading) && (
            <Box>
              <Title size={"lg"} header={t("tasks:section_title_completed")} paddingBottom />
              {isCompletedLoading ? (
                <Stack gap={"sm"}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className={"h-20 w-auto"} />
                  ))}
                </Stack>
              ) : (
                completedGroups.map((group) => (
                  <Stack key={group.date.toISOString()} gap={"lg"}>
                    <TasksSectionContent
                      title={renderMonthYear(group.date)}
                      tasks={group.tasks}
                      isLoading={false}
                    />
                  </Stack>
                ))
              )}
              <PaginationBlock
                metadata={completedData?.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            </Box>
          )}
        </Stack>
      ) : (
        <EmptyStateBase
          emoji={emojiDefaults.task.default}
          title={t("tasks:section_empty_title_todo")}
          description={t(emptyDescriptionKeyMap[role])}
          primaryButton={
            canCreateTask && (
              <Button
                variant={"secondary"}
                onClick={handleCreateTask}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              >
                {t("tasks:create_task")}
              </Button>
            )
          }
        />
      )}
    </Stack>
  );
}
